import { Alert, Anchor, Badge, Button, Card, Group, Stack, Switch, Text, Title } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconBook, IconExternalLink, IconInfoCircle } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { apiFetch } from '../../hooks/useApi'

export const Route = createFileRoute('/dev/docs')({
  component: DocsManagerPage,
})

function DocsManagerPage() {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['dev', 'docs-status'],
    queryFn: () => apiFetch<{ enabled: boolean }>('/api/dev/docs-status'),
  })

  const toggle = useMutation({
    mutationFn: () => apiFetch<{ enabled: boolean }>('/api/dev/docs-toggle', { method: 'POST' }),
    onSuccess: (res) => {
      qc.setQueryData(['dev', 'docs-status'], res)
      notifications.show({
        title: res.enabled ? 'Docs diaktifkan' : 'Docs dinonaktifkan',
        message: res.enabled ? '/docs sekarang bisa diakses' : '/docs sudah ditutup',
        color: res.enabled ? 'green' : 'orange',
      })
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Gagal mengubah status docs', color: 'red' })
    },
  })

  const enabled = data?.enabled ?? false
  const docsUrl = `${window.location.origin}/docs`

  return (
    <Stack gap="lg" maw={560}>
      <Title order={4}>Docs Manager</Title>

      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" component="div">
          <strong>/docs</strong> hanya tersedia di{' '}
          <Badge size="xs" variant="dot" color="yellow">
            staging
          </Badge>{' '}
          dan{' '}
          <Badge size="xs" variant="dot" color="green">
            development
          </Badge>
          . Di production, endpoint ini tidak ada sama sekali.
        </Text>
      </Alert>

      <Card withBorder radius="md" p="lg">
        <Stack gap="md">
          <Group justify="space-between">
            <Stack gap={2}>
              <Text fw={600}>API Documentation</Text>
              <Text size="sm" c="dimmed">
                Scalar UI — OpenAPI 3.0 interaktif
              </Text>
            </Stack>
            <Switch
              size="lg"
              checked={enabled}
              onChange={() => toggle.mutate()}
              disabled={isLoading || toggle.isPending}
              onLabel="ON"
              offLabel="OFF"
              color="green"
            />
          </Group>

          {enabled && (
            <Group gap="xs">
              <Anchor href={docsUrl} target="_blank" size="sm">
                {docsUrl}
              </Anchor>
              <IconExternalLink size={14} style={{ opacity: 0.5 }} />
            </Group>
          )}

          {!enabled && (
            <Text size="sm" c="dimmed">
              Aktifkan toggle untuk membuka akses ke /docs.
            </Text>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" p="lg" bg="var(--mantine-color-default-hover)">
        <Stack gap="xs">
          <Group gap="xs">
            <IconBook size={16} />
            <Text size="sm" fw={600}>
              Cara menggunakan
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            1. Aktifkan toggle di atas
          </Text>
          <Text size="sm" c="dimmed">
            2. Buka <strong>/docs</strong> di browser
          </Text>
          <Text size="sm" c="dimmed">
            3. Di panel <strong>Authentication</strong> → pilih <strong>bearerAuth</strong> → masukkan API key (generate
            di halaman{' '}
            <Anchor href="/dev/api-keys" size="sm">
              API Keys
            </Anchor>
            )
          </Text>
          <Text size="sm" c="dimmed">
            4. Test endpoint langsung dari Scalar UI
          </Text>
        </Stack>
      </Card>
    </Stack>
  )
}
